import { useCallback, useEffect, useState } from "preact/hooks";
import { getSetting, setSetting } from "../character";
import type { LinkResolver } from "../data/links";
import { loadLinkResolver } from "../data/links";
import { EntryViewer, type ViewerRequest } from "./entry-viewer";
import { Library } from "./library";
import { requestLabel, type RollRequest } from "./rolls";
import { navigate, type LibrarySection } from "./router";
import { SheetContext, type OpenEntryOptions } from "./sheet-context";
import { ThemeControl } from "./theme-control";

/** The Library on its own page (#15), reached from the home screen: no Character, so Roll buttons only show their numbers. */
export function LibraryPage({ section }: { section: LibrarySection }) {
  const [legacy, setLegacy] = useState(false);
  const [links, setLinks] = useState<LinkResolver | null>(null);
  const [viewer, setViewer] = useState<ViewerRequest | null>(null);
  const [message, setMessage] = useState<string>();
  useEffect(() => {
    getSetting("showLegacy").then((on) => setLegacy(on === true), () => undefined);
    loadLinkResolver().then(setLinks, () => setLinks(null));
  }, []);
  const onLegacy = (on: boolean) => {
    setLegacy(on);
    void setSetting("showLegacy", on).catch(() => undefined);
  };
  const openEntry = useCallback((uuid: string, options: OpenEntryOptions = {}) => setViewer({ uuid, ...options }), []);
  const roll = useCallback((request: RollRequest) => setMessage(`${requestLabel(request)}: open a Character to roll with its numbers.`), []);
  return (
    <SheetContext.Provider value={{ openEntry, roll }}>
      <div class="sheet library-page">
        <header class="sheet-header">
          <a href="#/" class="icon back" aria-label="Characters">‹</a>
          <div class="sheet-title">
            <h1>Library</h1>
            <p class="muted">Every shipped Pack, offline</p>
          </div>
          <ThemeControl />
        </header>
        {message && (
          <p class="notice sheet-notice" role="status">
            {message} <button type="button" class="linkish" onClick={() => setMessage(undefined)}>dismiss</button>
          </p>
        )}
        <main class="sheet-body">
          <Library section={section} onSection={(s) => navigate({ page: "library", section: s })} legacy={legacy} onLegacy={onLegacy} />
        </main>
        <EntryViewer request={viewer} links={links} actor={null} onRoll={roll} onClose={() => setViewer(null)} />
      </div>
    </SheetContext.Provider>
  );
}
